import React from 'react';
import {
  ArrowLeft,
  Loader2,
  Search,
} from 'lucide-react';

// 50 US states + DC — keys match the backend US_STATES_50 whitelist. Labels
// are the full name for the picker; the value is what we send to the API.
export const US_STATE_OPTIONS = [
  ['AL', 'Alabama'], ['AK', 'Alaska'], ['AZ', 'Arizona'], ['AR', 'Arkansas'],
  ['CA', 'California'], ['CO', 'Colorado'], ['CT', 'Connecticut'], ['DE', 'Delaware'],
  ['DC', 'District of Columbia'], ['FL', 'Florida'], ['GA', 'Georgia'], ['HI', 'Hawaii'],
  ['ID', 'Idaho'], ['IL', 'Illinois'], ['IN', 'Indiana'], ['IA', 'Iowa'],
  ['KS', 'Kansas'], ['KY', 'Kentucky'], ['LA', 'Louisiana'], ['ME', 'Maine'],
  ['MD', 'Maryland'], ['MA', 'Massachusetts'], ['MI', 'Michigan'], ['MN', 'Minnesota'],
  ['MS', 'Mississippi'], ['MO', 'Missouri'], ['MT', 'Montana'], ['NE', 'Nebraska'],
  ['NV', 'Nevada'], ['NH', 'New Hampshire'], ['NJ', 'New Jersey'], ['NM', 'New Mexico'],
  ['NY', 'New York'], ['NC', 'North Carolina'], ['ND', 'North Dakota'], ['OH', 'Ohio'],
  ['OK', 'Oklahoma'], ['OR', 'Oregon'], ['PA', 'Pennsylvania'], ['RI', 'Rhode Island'],
  ['SC', 'South Carolina'], ['SD', 'South Dakota'], ['TN', 'Tennessee'], ['TX', 'Texas'],
  ['UT', 'Utah'], ['VT', 'Vermont'], ['VA', 'Virginia'], ['WA', 'Washington'],
  ['WV', 'West Virginia'], ['WI', 'Wisconsin'], ['WY', 'Wyoming'],
];

/**
 * Step 2 — area-code / multi-state search inputs.
 * @param {Object} props - Controlled search state, field errors and onBack/onSearch handlers.
 */
export default function StepSearch({
  numberType, searchMode, setSearchMode,
  areaCode, setAreaCode, states, setStates,
  fieldErrors, searching, onBack, onSearch,
}) {
  const isLocal = numberType === 'local';
  const isStatesMode = isLocal && searchMode === 'states';

  function toggleState(code) {
    setStates((prev) => (prev.includes(code) ? prev.filter((s) => s !== code) : [...prev, code]));
  }
  function selectAllStates() { setStates(US_STATE_OPTIONS.map(([c]) => c)); }
  function clearStates() { setStates([]); }

  return (
    <div className="space-y-4">
      <div>
        <div className="font-display font-bold text-lg">
          {numberType === 'tollFree'
            ? 'Search toll-free numbers'
            : (isStatesMode ? 'Search by state' : 'Search by area code')}
        </div>
        <div className="text-xs text-white/55">We'll fetch up to 20 available numbers from Twilio.</div>
      </div>

      {isLocal && (
        <div
          role="radiogroup"
          aria-label="Search filter mode"
          className="inline-flex rounded-md border border-civix-border/50 bg-black/20 p-1 text-xs"
        >
          <button
            type="button"
            role="radio"
            aria-checked={searchMode === 'areaCode'}
            onClick={() => setSearchMode('areaCode')}
            className={`px-3 py-1.5 rounded-sm transition ${searchMode === 'areaCode' ? 'bg-civix-purple/30 text-white' : 'text-white/60 hover:text-white'}`}
          >
            Area code
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={searchMode === 'states'}
            onClick={() => setSearchMode('states')}
            className={`px-3 py-1.5 rounded-sm transition ${searchMode === 'states' ? 'bg-civix-purple/30 text-white' : 'text-white/60 hover:text-white'}`}
          >
            States
          </button>
        </div>
      )}

      {isLocal && searchMode === 'areaCode' && (
        <div className="max-w-xs">
          <label className="label" htmlFor="ac-input">US area code</label>
          <input
            id="ac-input"
            inputMode="numeric"
            maxLength={3}
            placeholder="e.g. 720"
            value={areaCode}
            onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, '').slice(0, 3))}
            aria-invalid={!!fieldErrors.areaCode}
            aria-describedby={fieldErrors.areaCode ? 'ac-err' : undefined}
            className={`input mt-1 ${fieldErrors.areaCode ? 'border-rose-500/70' : ''}`}
          />
          {fieldErrors.areaCode && (
            <div id="ac-err" className="text-xs text-rose-400 mt-1.5">{fieldErrors.areaCode}</div>
          )}
        </div>
      )}

      {isStatesMode && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="label">Select US states ({states.length} selected)</label>
            <div className="flex items-center gap-3 text-xs">
              <button type="button" onClick={selectAllStates} className="text-civix-purple hover:underline">Select all</button>
              <button type="button" onClick={clearStates} className="text-white/50 hover:text-white/80">Clear</button>
            </div>
          </div>
          <div
            role="listbox"
            aria-multiselectable="true"
            aria-invalid={!!fieldErrors.states}
            aria-describedby={fieldErrors.states ? 'states-err' : undefined}
            className={`grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1.5 max-h-64 overflow-y-auto p-2 rounded-md border ${fieldErrors.states ? 'border-rose-500/70' : 'border-civix-border/50'} bg-black/20`}
          >
            {US_STATE_OPTIONS.map(([code, label]) => {
              const checked = states.includes(code);
              return (
                <label
                  key={code}
                  className={`flex items-center gap-2 px-2 py-1 rounded-sm cursor-pointer text-xs ${checked ? 'bg-civix-purple/20 text-white' : 'text-white/70 hover:bg-white/5'}`}
                >
                  <input
                    type="checkbox"
                    className="accent-civix-purple"
                    checked={checked}
                    onChange={() => toggleState(code)}
                  />
                  <span className="font-mono">{code}</span>
                  <span className="text-white/40 truncate">{label}</span>
                </label>
              );
            })}
          </div>
          {fieldErrors.states && (
            <div id="states-err" className="text-xs text-rose-400 mt-1.5">{fieldErrors.states}</div>
          )}
          <div className="mt-3 max-w-xs">
            <label className="label" htmlFor="ac-narrow">Narrow by area code (optional)</label>
            <input
              id="ac-narrow"
              inputMode="numeric"
              maxLength={3}
              placeholder="e.g. 720"
              value={areaCode}
              onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, '').slice(0, 3))}
              className="input mt-1"
            />
            <div className="text-[11px] text-white/45 mt-1">Restricts results within the selected states to this area code.</div>
          </div>
        </div>
      )}

      {!isLocal && (
        <div className="text-sm text-white/70">No area code needed for toll-free — we'll fetch any available 8XX numbers.</div>
      )}

      <div className="flex items-center justify-between pt-1">
        <button onClick={onBack} className="btn-ghost inline-flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <button
          onClick={onSearch}
          disabled={searching}
          className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
        >
          {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          {searching ? 'Searching…' : 'Search'}
        </button>
      </div>
    </div>
  );
}
